
export const defaultColor = '#4C58FC';

interface RGBA {
  r: number;
  g: number;
  b: number;
  a: number;
}

export const rgba2hex = (rgba: RGBA): string => {
  const { r, g, b, a } = rgba;
  const hex = (r | 1 << 8).toString(16).slice(1) +
    (g | 1 << 8).toString(16).slice(1) +
    (b | 1 << 8).toString(16).slice(1);
  const alpha = ((a * 255) | 1 << 8).toString(16).slice(1);
  return `#${hex}${alpha === 'ff' ? '' : alpha}`;
};

export function validateColor(widgetColor: string): string {
  try {
    if (widgetColor.startsWith("rgb(") && widgetColor.endsWith(")")) {
      const rgb = widgetColor
        .match(/\( *([0-9]+) *, *([0-9]+) *, *([0-9]+) *\)/)
        ?.slice(1)
        .map((x: string) => parseFloat(x)) || [];
      if (rgb.length === 3) {
        const [r, g, b] = rgb;
        widgetColor = rgba2hex({ r, g, b, a: 1.0 });
      } else {
        widgetColor = defaultColor;
      }
    } else if (widgetColor.startsWith("rgba(") && widgetColor.endsWith(")")) {
      const rgba = widgetColor
        .match(/\( *([0-9]+) *, *([0-9]+) *, *([0-9]+) *, *([0-9.]+) *\)/)
        ?.slice(1)
        .map((x: string) => parseFloat(x)) || [];
      if (rgba.length === 4) {
        const [r, g, b, a] = rgba;
        widgetColor = rgba2hex({ r, g, b, a });
      } else {
        widgetColor = defaultColor;
      }
    } else if (widgetColor.startsWith('#')) {
      if ([4, 5, 7, 9].indexOf(widgetColor.length) === -1) {
        widgetColor = defaultColor;
      }
      if (widgetColor.slice(1).match(/[^0-9a-fA-F]/) !== null) {
        widgetColor = defaultColor;
      }
    } else {
      widgetColor = defaultColor;
    }
  } catch (e) {
    // For whatever reason, if it wasn't unparsable.
    console.error(e);
    widgetColor = defaultColor;
  }
  return widgetColor;
}

export function hexToHSL(color: string): number[] {
  let c = color.substring(1);      // strip #

  if (c.length === 3) {
    c = c + c;
  } else if (c.length === 4) {
    const trunc = c.substring(c.length - 1, -1);
    c = trunc + trunc;
  }

  const rgb = parseInt(c, 16);   // convert rrggbb to decimal
  const r = (rgb >> 16) & 0xff;  // extract red
  const g = (rgb >> 8) & 0xff;  // extract green
  const b = (rgb >> 0) & 0xff;  // extract blue

  return rgbToHSL(r, g, b);
}

function rgbToHSL(r: number, g: number, b: number): number[] {
  r /= 255;
  g /= 255;
  b /= 255;
  const l = Math.max(r, g, b);
  const s = l - Math.min(r, g, b);
  const h = s
    ? l === r
      ? (g - b) / s
      : l === g
        ? 2 + (b - r) / s
        : 4 + (r - g) / s
    : 0;
  return [
    60 * h < 0 ? 60 * h + 360 : 60 * h,
    100 * (s ? (l <= 0.5 ? s / (2 * l - s) : s / (2 - (2 * l - s))) : 0),
    (100 * (2 * l - s)) / 2,
  ];
}

export function lightenColor(color: string, lightness = 92.94) {
  const hslColor = hexToHSL(color);
  return `hsl(${hslColor[0]}deg ${hslColor[1]}% ${lightness}%)`;
}

export function darkenColor(color: string): string {
  const hslColor = hexToHSL(color);
  return `hsl(${hslColor[0]}deg ${hslColor[1]}% ${hslColor[2] - 10}%)`;
}

export function calculateFontColor(color: string): string {
  let c = color.substring(1);      // strip #

  if (c.length === 3) {
    c = c + c;
  } else if (c.length === 4) {
    const trunc = c.substring(c.length - 1, -1);
    c = trunc + trunc;
  }

  const rgb = parseInt(c, 16);   // convert rrggbb to decimal
  const r = (rgb >> 16) & 0xff;  // extract red
  const g = (rgb >> 8) & 0xff;  // extract green
  const b = (rgb >> 0) & 0xff;  // extract blue

  const luma = 0.2126 * r + 0.7152 * g + 0.0722 * b; // per ITU-R BT.709

  if (luma < 180) {
    return '#FFFFFF';
  } else {
    return '#0F0F0F';
  }
}
